import styled from "@emotion/styled";
import type { FC } from "react";
import { useMemo } from "react";
import {
  IconHeartInHands,
  IconWeightScale,
  ProgramIcon
} from "src/constants/icons";
import { AppQueryPopupsController } from "src/ui/components/AppQueryPopups/AppQueryPopupsBloc";
import FaqComponent from "src/ui/components/FaqComponent/FaqComponent";
import InDialog from "src/ui/components/InDialog/InDialog";
import useGoToOrBack from "src/ui/hooks/useGoToOrBack";
import Translate from "src/ui/components/Translate/Translate";
import logo from "src/ui/assets/icons/9am-logo.svg";
import translate from "src/lib/translate";
import clsx from "clsx";
import { userState } from "src/state/state";
import { ProfileProgramMembership } from "src/constants/programs";
import { PayerId } from "src/constants/payers";
import { useBlocNext } from "@blac/react";
import AppViewCubit from "src/state/AppViewCubit/AppViewCubit";

const GradientContainer = styled.div`
  position: relative;
  z-index: 1;
  margin-top: calc(var(--padding-top) * -1);
  margin-right: calc(var(--padding-end) * -1);
  margin-left: calc(var(--padding-start) * -1);
  padding-top: 2rem;
  padding-right: 1.5rem;
  padding-left: 1.5rem;
  padding-bottom: 2rem;
  background: var(--gradient-sunrise-light-vertical);
  background-size: 100% 200%;
  background-position-y: center;

  &.with-partner-session-styles {
    background: none;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;

  img {
    width: 4rem;
    height: 4rem;
  }
`;

const Title = styled.h4`
  text-align: center;
`;

const Description = styled.p`
  text-align: center;
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-bottom: 3rem;
`;

const Item = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const IconContainer = styled.div`
  width: 3rem;
  height: 3rem;
  background: var(--color-cream-darker);
  display: flex;
  border-radius: 0.5rem;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    color: var(--color-charcoal);
  }
`;

const BottomCreamGradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 7rem;
  background: linear-gradient(to bottom, transparent, var(--color-cream));
  z-index: -1;
`;

const Content = styled.div`
  flex: 1;
`;

const ItemTitle = styled.h5`
  margin: 0 0 0.5rem 0;
  color: var(--color-charcoal);
`;

const ItemText = styled.p`
  margin: 0;
`;

const ConsistencyScoreInfoPopup: FC<{
  returnUrl: string;
}> = ({ returnUrl }) => {
  const navigate = useGoToOrBack();
  const [{ partnerStyles }] = useBlocNext(AppViewCubit);

  const shouldSeeHWJContent = useMemo(() => {
    // if user is Evernorth user, return true
    const isEvernorthUser = userState.partnerId === PayerId.EVERNORTH;

    if (isEvernorthUser) {
      return true;
    }

    // Check if user is HWJ (Healthy Weight Journey)
    const isHWJUser = userState.programMemberships?.some(
      (membership) =>
        membership.program === ProfileProgramMembership.HEALTHY_WEIGHT_JOURNEY
    );

    return isHWJUser;
  }, []);

  const translateVariables = shouldSeeHWJContent
    ? undefined
    : { context: "non_hwj" };

  const onClose = () => {
    AppQueryPopupsController.closePopup();
    navigate(returnUrl, {
      multiBack: true,
      replace: true
    });
  };

  return (
    <InDialog
      popup
      title={translate("consistency_score.info.title")}
      onClose={onClose}
      returnUrl={returnUrl}
    >
      <GradientContainer
        className={clsx({
          "with-partner-session-styles": partnerStyles
        })}
      >
        <LogoContainer>
          <img src={logo} alt="" aria-hidden="true" />
        </LogoContainer>

        <Title>
          <Translate msg="consistency_score.info.title" />
        </Title>

        <Description className="as-body">
          <Translate msg="consistency_score.info.description" />
        </Description>
        {!partnerStyles && <BottomCreamGradient />}
      </GradientContainer>

      <ItemsList>
        <Item>
          <IconContainer>
            <IconHeartInHands width={24} height={24} />
          </IconContainer>
          <Content>
            <ItemTitle className="as-body2">
              <Translate msg="consistency_score.info.start_fresh.title" />
            </ItemTitle>
            <ItemText className="as-little">
              <Translate msg="consistency_score.info.start_fresh.description" />
            </ItemText>
          </Content>
        </Item>

        <Item>
          <IconContainer>
            <IconWeightScale />
          </IconContainer>
          <Content>
            <ItemTitle className="as-body2">
              <Translate
                msg="consistency_score.info.vitals_tracking.title"
                variables={translateVariables}
              />
            </ItemTitle>
            <ItemText className="as-little">
              <Translate
                msg="consistency_score.info.vitals_tracking.description"
                variables={translateVariables}
              />
            </ItemText>
          </Content>
        </Item>

        <Item>
          <IconContainer>
            <ProgramIcon />
          </IconContainer>
          <Content>
            <ItemTitle className="as-body2">
              <Translate msg="consistency_score.info.program_engagement.title" />
            </ItemTitle>
            <ItemText className="as-little">
              <Translate msg="consistency_score.info.program_engagement.description" />
            </ItemText>
          </Content>
        </Item>
      </ItemsList>

      <FaqComponent
        context={
          shouldSeeHWJContent ? "consistencyScoreHWJ" : "consistencyScoreNonHWJ"
        }
      />
    </InDialog>
  );
};

export default ConsistencyScoreInfoPopup;
